import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Container, AppBar, Toolbar, Typography, Box, Tabs, Tab,
    Button, Card, CardContent, CardMedia, Grid, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, ListItemSecondaryAction,
    IconButton, TextField, Select, MenuItem, FormControl, InputLabel,
    Paper, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, Stack, Divider, ThemeProvider, createTheme,
    CssBaseline, useMediaQuery, useTheme, Drawer, ListSubheader,
    CircularProgress, LinearProgress, Fade, Collapse, Grow, Checkbox, Badge, Tooltip, Alert
} from '@mui/material';
import {
    Delete, PlayArrow, CloudUpload, Folder, Edit, Save, ContentCopy,
    EventNote, Add, Language, Dashboard, Computer,
    PlaylistPlay, FolderSpecial, ChevronLeft, ChevronRight,
    Logout, ViewList, Map as MapIcon, Tv, Dns, History,
    FolderOpen, RestartAlt, Pause, NotificationsActive, NotificationsOff, Send
} from '@mui/icons-material';

// Skeleton Component for Loading State
const DeviceSkeleton = () => (
    <Grid item xs={12} sm={6} md={4}>
        <GlassContainer sx={{ p: 0, overflow: 'hidden', height: 280, opacity: 0.5 }}>
            <Box sx={{ height: 10, bgcolor: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ p: 3 }}>
                <Skeleton variant="text" width="60%" height={32} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 1 }} />
                <Skeleton variant="text" width="40%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 3 }} />
                <Skeleton variant="rectangular" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, mb: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={6}><Skeleton variant="rectangular" height={30} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }} /></Grid>
                    <Grid item xs={6}><Skeleton variant="rectangular" height={30} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }} /></Grid>
                </Grid>
            </Box>
        </GlassContainer>
    </Grid>
);

const Skeleton = ({ variant, width, height, sx }) => (
    <Box sx={{
        width, height,
        borderRadius: variant === 'rectangular' ? 1 : 0.5,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'rgba(255,255,255,0.03)',
        ...sx,
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
            animation: 'shimmer 1.5s infinite',
        },
        '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' }
        }
    }} />
);


import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Rnd } from 'react-rnd';
import TimelineEditor from './TimelineEditor';
import { theme } from './theme';
import GlassContainer from './components/GlassContainer';

class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    componentDidCatch(error, errorInfo) { console.error("Uncaught error:", error, errorInfo); }
    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{ p: 5, textAlign: 'center', color: 'error.main' }}>
                    <Typography variant="h5" gutterBottom>Algo salió mal.</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.5)', p: 2, borderRadius: 1 }}>
                        {this.state.error && this.state.error.toString()}
                    </Typography>
                </Box>
            );
        }
        return this.props.children;
    }
}

const API_URL = 'api';

// Global Styles
const GlobalStyles = () => (
    <style dangerouslySetInnerHTML={{
        __html: `
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800;900&display=swap');
        html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; background: #050510; }
        #root { height: 100%; }
        .abstract-bg { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: -1; overflow: hidden; }
        .orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.15; animation: float 20s infinite alternate; }
        @keyframes float { 0% { transform: translate(0, 0) scale(1); } 100% { transform: translate(100px, 50px) scale(1.2); } }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); }
        ::-webkit-scrollbar-thumb { background: rgba(59, 130, 246, 0.3); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(59, 130, 246, 0.5); }
    `}} />
);

const DRAWER_WIDTH = 240;

const SidebarItem = ({ icon, label, active, onClick, collapsed }) => (
    <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
        <ListItemButton
            onClick={onClick}
            sx={{
                minHeight: 48,
                justifyContent: collapsed ? 'center' : 'initial',
                px: 2.5,
                mx: 1,
                borderRadius: 2,
                bgcolor: active ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                color: active ? '#3b82f6' : 'rgba(255,255,255,0.6)',
                '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: active ? '#60a5fa' : 'white'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                    color: 'inherit'
                }}
            >
                {icon}
            </ListItemIcon>
            {!collapsed && <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 800, fontSize: '0.85rem', letterSpacing: 0.5 }} />}
        </ListItemButton>
    </ListItem>
);

function DashboardOverview({ devices, layouts }) {
    const online = devices.filter(d => d.status === 'online').length;
    const offline = devices.length - online;
    return (
        <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 4 }}>Control de Operaciones</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <GlassContainer sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="overline" color="textSecondary">PANTALLAS ONLINE</Typography>
                        <Typography variant="h2" fontWeight={900} sx={{ color: '#10b981' }}>{online}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>DE {devices.length} TOTALES</Typography>
                    </GlassContainer>
                </Grid>
                <Grid item xs={12} md={4}>
                    <GlassContainer sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="overline" color="textSecondary">ALERTAS ACTIVAS</Typography>
                        <Typography variant="h2" fontWeight={900} sx={{ color: offline > 0 ? '#ef4444' : 'white' }}>{offline}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>NODOS SIN RESPUESTA</Typography>
                    </GlassContainer>
                </Grid>
                <Grid item xs={12} md={4}>
                    <GlassContainer sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="overline" color="textSecondary">DISEÑOS PRO</Typography>
                        <Typography variant="h2" fontWeight={900} sx={{ color: '#3b82f6' }}>{layouts.length}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>ESTRUCTURAS STORYBOARD</Typography>
                    </GlassContainer>
                </Grid>
            </Grid>
        </Box>
    );
}

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('authToken'));
    const [tab, setTab] = useState(0); // 0: Dashboard, 1: Devices, 2: Canales (NLE), 3: Recursos
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [media, setMedia] = useState([]);
    const [layouts, setLayouts] = useState([]);
    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [openAlerts, setOpenAlerts] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            axios.defaults.headers.common['Authorization'] = token;
        }

        const reqInterceptor = axios.interceptors.request.use(config => {
            const currentToken = localStorage.getItem('authToken');
            if (currentToken) {
                if (config.headers && config.headers.set) {
                    config.headers.set('Authorization', currentToken);
                } else {
                    config.headers['Authorization'] = currentToken;
                }
            }
            return config;
        });
        const resInterceptor = axios.interceptors.response.use(
            r => r,
            error => {
                if (error.response?.status === 401) handleLogout();
                return Promise.reject(error);
            }
        );

        if (isAuthenticated) {
            fetchAllData();
            const intervalDevices = setInterval(() => fetchDevices(true), 5000); // Silent poll
            const intervalLayouts = setInterval(() => fetchContent(true), 15000); // Silent poll
            
            return () => { 
                clearInterval(intervalDevices); 
                clearInterval(intervalLayouts); 
                axios.interceptors.request.eject(reqInterceptor);
                axios.interceptors.response.eject(resInterceptor);
            };
        } else {
            return () => {
                axios.interceptors.request.eject(reqInterceptor);
                axios.interceptors.response.eject(resInterceptor);
            };
        }
    }, [isAuthenticated]);

    const fetchAllData = async () => {
        setIsLoading(true);
        setIsSyncing(true);
        try {
            await Promise.all([fetchDevices(false), fetchContent(false), fetchMedia(false)]);
        } catch (e) { console.error(e); }
        setIsLoading(false);
        setIsSyncing(false);
    };

    // silent=true prevents full screen loader and syncing indicators
    const fetchDevices = async (silent = false) => {
        if (!silent) { setIsLoading(true); setIsSyncing(true); }
        try {
            const res = await axios.get(`${API_URL}/devices`);
            if (Array.isArray(res.data)) setDevices(res.data);
        } catch (e) { console.error(e); }
        if (!silent) { setIsLoading(false); setIsSyncing(false); }
    };

    const fetchContent = async (silent = false) => {
        if (!silent) { setIsLoading(true); setIsSyncing(true); }
        try {
            const res = await axios.get(`${API_URL}/layouts`);
            if (Array.isArray(res.data)) setLayouts(res.data);
        } catch (e) { console.error(e); }
        if (!silent) { setIsLoading(false); setIsSyncing(false); }
    };

    const fetchMedia = async (silent = false) => {
        if (!silent) { setIsLoading(true); setIsSyncing(true); }
        try {
            const res = await axios.get(`${API_URL}/media`);
            if (Array.isArray(res.data)) setMedia(res.data);
        } catch (e) { console.error(e); }
        if (!silent) { setIsLoading(false); setIsSyncing(false); }
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        delete axios.defaults.headers.common['Authorization'];
        setIsAuthenticated(false);
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles />
            {!isAuthenticated ? (
                <LoginScreen onLogin={t => { 
                    localStorage.setItem('authToken', t); 
                    axios.defaults.headers.common['Authorization'] = t;
                    setIsAuthenticated(true); 
                }} />
            ) : (
                <Box sx={{ display: 'flex', bgcolor: '#050510', minHeight: '100vh', color: 'white' }}>
                    {/* Enterprise Sidebar */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            width: sidebarCollapsed ? 80 : DRAWER_WIDTH,
                            flexShrink: 0,
                            '& .MuiDrawer-paper': {
                                width: sidebarCollapsed ? 80 : DRAWER_WIDTH,
                                boxSizing: 'border-box',
                                bgcolor: 'rgba(10, 10, 25, 0.8)',
                                backdropFilter: 'blur(20px)',
                                borderRight: '1px solid rgba(255,255,255,0.05)',
                                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                overflowX: 'hidden'
                            },
                        }}
                    >
                        <Box sx={{ p: 3, mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 32, height: 32, bgcolor: '#3b82f6', borderRadius: 1.5, flexShrink: 0, boxShadow: '0 0 15px rgba(59, 130, 246, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Tv sx={{ color: 'white', fontSize: 20 }} />
                            </Box>
                            {!sidebarCollapsed && <Typography variant="h6" fontWeight={900} letterSpacing={-1}>Accio<span style={{ color: '#3b82f6' }}>TV</span></Typography>}
                        </Box>

                        <List sx={{ px: 1 }}>
                            <SidebarItem icon={<Dashboard />} label="DASHBOARD" active={tab === 0} onClick={() => setTab(0)} collapsed={sidebarCollapsed} />
                            <SidebarItem icon={<Computer />} label="TERMINALES" active={tab === 1} onClick={() => setTab(1)} collapsed={sidebarCollapsed} />
                            <SidebarItem icon={<PlaylistPlay />} label="CANALES" active={tab === 2} onClick={() => setTab(2)} collapsed={sidebarCollapsed} />
                            <SidebarItem icon={<CloudUpload />} label="RECURSOS" active={tab === 3} onClick={() => setTab(3)} collapsed={sidebarCollapsed} />
                            <SidebarItem
                                icon={
                                    <Badge badgeContent={devices.filter(d => d.status === 'offline').length || 0} color="error" max={9}>
                                        <NotificationsActive />
                                    </Badge>
                                }
                                label="ALERTAS"
                                active={openAlerts}
                                onClick={() => setOpenAlerts(true)}
                                collapsed={sidebarCollapsed}
                            />
                        </List>

                        <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                            <SidebarItem
                                icon={sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                                label="CONTRAER"
                                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                collapsed={sidebarCollapsed}
                            />
                            <SidebarItem
                                icon={<Logout />}
                                label="SALIR"
                                onClick={handleLogout}
                                collapsed={sidebarCollapsed}
                            />
                        </Box>
                    </Drawer>

                    {/* Main Content Area */}
                    <Box component="main" sx={{ flexGrow: 1, p: 0, overflowY: 'auto', height: '100vh', position: 'relative' }}>
                        <div className="abstract-bg">
                            <div className="orb" style={{ top: '-10%', left: '10%', background: '#3b82f6', width: 400, height: 400 }} />
                            <div className="orb" style={{ bottom: '20%', right: '10%', background: '#10b981', width: 500, height: 500 }} />
                        </div>

                        <AppBar position="sticky" sx={{ bgcolor: 'rgba(5, 5, 16, 0.7)', backdropFilter: 'blur(10px)', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Toolbar variant="dense">
                                <Typography variant="overline" sx={{ fontWeight: 900, color: 'rgba(255,255,255,0.5)', letterSpacing: 2, flexGrow: 1 }}>
                                    {['PANEL DE CONTROL', 'MONITOR DE RED', 'CANALES Y DISEÑOS', 'RECURSOS MULTIMEDIA'][tab]}
                                </Typography>
                                {devices.filter(d => d.status === 'offline').length > 0 && (
                                    <Fade in={true}>
                                        <Chip
                                            label={`${devices.filter(d => d.status === 'offline').length} OFFLINE`}
                                            size="small"
                                            onClick={() => setOpenAlerts(true)}
                                            sx={{ mr: 2, bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 900, fontSize: '0.6rem', cursor: 'pointer', border: '1px solid rgba(239,68,68,0.3)', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.4)' }, '70%': { boxShadow: '0 0 0 6px rgba(239,68,68,0)' }, '100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0)' } } }}
                                        />
                                    </Fade>
                                )}
                                {isSyncing && (
                                    <Fade in={true}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.6 }}>
                                            <CircularProgress size={14} thickness={6} color="inherit" />
                                            <Typography variant="overline" sx={{ fontSize: '0.6rem', fontWeight: 900 }}>SINCRONIZANDO</Typography>
                                        </Box>
                                    </Fade>
                                )}
                                <Tooltip title="Configurar Alertas Telegram">
                                    <IconButton size="small" onClick={() => setOpenAlerts(true)} sx={{ ml: 1, color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#3b82f6' } }}>
                                        <NotificationsActive fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Toolbar>
                            {isSyncing && <LinearProgress sx={{ height: 2, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />}
                        </AppBar>

                        <Container maxWidth={tab === 2 ? false : "xl"} disableGutters={tab === 2} sx={{ py: 4, px: tab === 2 ? 0 : 4, minHeight: '60vh', display: 'flex', flexDirection: 'column' }}>
                            <ErrorBoundary>
                                {isLoading ? (
                                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                                        <CircularProgress size={64} thickness={4} sx={{ color: '#3b82f6', mb: 3 }} />
                                        <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: 1, mb: 1 }}>Sincronizando Sistema</Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Cargando terminales y recursos multimedia...</Typography>
                                    </Box>
                                ) : (
                                    <Fade in={true} timeout={500}>
                                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            {tab === 0 && <DashboardOverview devices={devices} layouts={layouts} />}
                                            {tab === 1 && <DevicesTab devices={devices} layouts={layouts} onRefresh={() => fetchDevices(false)} />}
                                            {tab === 2 && <LayoutsTab layouts={layouts} onRefresh={() => fetchContent(false)} onIdCreated={() => fetchContent(true)} />}
                                            {tab === 3 && <MediaTab media={media} onRefresh={() => fetchMedia(false)} />}
                                        </Box>
                                    </Fade>
                                )}
                            </ErrorBoundary>
                        </Container>
                    </Box>
                </Box>
            )}
            <AlertSettingsDialog open={openAlerts} onClose={() => setOpenAlerts(false)} offlineDevices={devices.filter(d => d.status === 'offline')} />
        </ThemeProvider>
    );
}

function LoginScreen({ onLogin }) {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loggingIn, setLoggingIn] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoggingIn(true);
        try {
            const res = await axios.post(`${API_URL}/login`, { password });
            if (res.data.success) onLogin(res.data.token);
            else { setError("Credenciales inválidas"); setLoggingIn(false); }
        } catch (err) { setError("Error de conexión o contraseña"); setLoggingIn(false); }
    };
    return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh" sx={{ background: 'radial-gradient(circle at top right, #0a0a20, #050510)' }}>
            <div className="abstract-bg">
                <div className="orb" style={{ top: '20%', left: '20%', background: '#3b82f6', width: 600, height: 600 }} />
            </div>
            <GlassContainer sx={{ p: 6, minWidth: 400 }}>
                <Typography variant="h4" align="center" sx={{ fontWeight: 900, mb: 4, letterSpacing: -1 }}>
                    <span style={{ color: '#3b82f6' }}>AccioTV</span> Signage
                </Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth type="password" label="Clave de Acceso" value={password}
                        onChange={e => setPassword(e.target.value)} margin="normal"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        disabled={loggingIn}
                    />
                    {error && <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: 'center' }}>{error}</Typography>}
                    <Button
                        fullWidth variant="contained" type="submit" size="large"
                        disabled={loggingIn}
                        sx={{ mt: 4, py: 1.5, boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)' }}
                    >
                        {loggingIn ? <CircularProgress size={24} color="inherit" /> : "Iniciar Sesión"}
                    </Button>
                </form>
            </GlassContainer>
        </Box>
    );
}

const DevicesTab = ({ devices, layouts, onRefresh }) => {
    const [viewMode, setViewMode] = useState('list');
    const [collapsedFolders, setCollapsedFolders] = useState({});
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [openLogs, setOpenLogs] = useState(false);
    const [openSchedule, setOpenSchedule] = useState(false);
    
    // Novedades: bulk, search y filter
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('status');

    const sendCommand = (id, command) => {
        axios.post(`${API_URL}/devices/${id}/command`, { command })
            .then(() => alert(`Comando '${command}' enviado correctamente`))
            .catch(err => alert("Error al enviar comando: " + err.message));
    };

    const deleteDevice = (id) => {
        if (window.confirm('¿Seguro que deseas eliminar el terminal?')) {
            axios.delete(`${API_URL}/devices/${id}`).then(onRefresh);
        }
    };

    // Bulk actions
    const handleBulkCommand = async (cmd) => {
        if (!window.confirm(`¿Enviar comando '${cmd}' a ${selectedIds.length} terminales?`)) return;
        for (let id of selectedIds) {
            await axios.post(`${API_URL}/devices/${id}/command`, { command: cmd }).catch(console.error);
        }
        alert(`Comando '${cmd}' procesado para terminales seleccionados.`);
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const filteredDevices = devices.filter(d => 
        d.name.toLowerCase().includes(search.toLowerCase()) || 
        (d.ip || '').includes(search)
    ).sort((a, b) => {
        if (sortBy === 'status') {
            if (a.status === 'offline' && b.status === 'online') return -1;
            if (a.status === 'online' && b.status === 'offline') return 1;
            return a.name.localeCompare(b.name);
        }
        if (sortBy === 'cpu') return (b.cpu_usage || 0) - (a.cpu_usage || 0);
        return a.name.localeCompare(b.name);
    });

    const selectAll = () => {
        if (selectedIds.length === filteredDevices.length) setSelectedIds([]);
        else setSelectedIds(filteredDevices.map(d => d.id));
    };

    const groupedDevices = filteredDevices.reduce((acc, d) => {
        const folder = "Nodos Locales"; // Simple grouping for now
        if (!acc[folder]) acc[folder] = [];
        acc[folder].push(d);
        return acc;
    }, {});

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField 
                        size="small" placeholder="Buscar terminal..." 
                        value={search} onChange={e => setSearch(e.target.value)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}
                    />
                    <Select size="small" value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{ minWidth: 150 }}>
                        <MenuItem value="status">POR ESTADO</MenuItem>
                        <MenuItem value="name">ALFABÉTICO</MenuItem>
                        <MenuItem value="cpu">USO CPU</MenuItem>
                    </Select>
                    <IconButton onClick={onRefresh} sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                        <RestartAlt />
                    </IconButton>
                </Box>
                <Box>
                    <Button variant={viewMode === 'list' ? 'contained' : 'outlined'} onClick={() => setViewMode('list')} sx={{ mr: 1 }}>LISTA</Button>
                    <Button variant={viewMode === 'map' ? 'contained' : 'outlined'} onClick={() => setViewMode('map')} sx={{ mr: 1 }}>MAPA</Button>
                    <Button variant={viewMode === 'graph' ? 'contained' : 'outlined'} onClick={() => setViewMode('graph')}>GRAFO</Button>
                </Box>
            </Box>

            {selectedIds.length > 0 && (
                <Fade in={true}>
                    <Paper sx={{ mb: 3, p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3b82f6', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={800} color="#3b82f6">{selectedIds.length} TERMINALES SELECCIONADOS</Typography>
                        <Box>
                            <Button size="small" variant="outlined" color="warning" onClick={() => handleBulkCommand('RESTART')} sx={{ mr: 1 }} startIcon={<RestartAlt />}>REINICIAR</Button>
                            <Button size="small" variant="outlined" color="warning" onClick={() => handleBulkCommand('PAUSE')} sx={{ mr: 1 }} startIcon={<Pause />}>PAUSAR</Button>
                            <Button size="small" variant="outlined" color="success" onClick={() => handleBulkCommand('RESUME')} sx={{ mr: 2 }} startIcon={<PlayArrow />}>REANUDAR</Button>
                            <Button size="small" onClick={() => setSelectedIds([])} sx={{ color: 'rgba(255,255,255,0.5)' }}>DESELECCIONAR</Button>
                        </Box>
                    </Paper>
                </Fade>
            )}

            {viewMode === 'list' ? (
                <Box>
                    <Box sx={{ mb: 1, px: 1 }}>
                        <FormControlLabel 
                            control={<Checkbox checked={selectedIds.length === filteredDevices.length && filteredDevices.length > 0} indeterminate={selectedIds.length > 0 && selectedIds.length < filteredDevices.length} onChange={selectAll} sx={{ color: 'rgba(255,255,255,0.3)' }} />}
                            label={<Typography variant="caption" sx={{ fontWeight: 800 }}>SELECCIONAR TODOS</Typography>}
                        />
                    </Box>
                    {Object.keys(groupedDevices).map(folder => (
                        <Box key={folder} sx={{ mb: 3 }}>
                            {/* Folder toggle header */}
                            <Collapse in={!collapsedFolders[folder]}>
                                <Grid container spacing={3}>
                                    {groupedDevices[folder].map(d => (
                                        <Grid item xs={12} sm={6} md={4} key={d.id}>
                                            <DeviceCard 
                                                d={d} layouts={layouts} onRefresh={onRefresh} 
                                                selected={selectedIds.includes(d.id)}
                                                onToggleSelect={() => toggleSelect(d.id)}
                                                onLogs={() => { setSelectedDevice(d); setOpenLogs(true); }} 
                                                onSchedule={() => { setSelectedDevice(d); setOpenSchedule(true); }} 
                                                onDelete={() => deleteDevice(d.id)} 
                                                onCommand={sendCommand}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Collapse>
                        </Box>
                    ))}
                </Box>
            ) : viewMode === 'map' ? (
                <TopologyView devices={devices} onSelect={setSelectedDevice} onCommand={sendCommand} />
            ) : (
                <NodeGraphView devices={devices} layouts={layouts} />
            )}
            {/* ... rest of component */}

            <ScheduleDialog
                open={openSchedule}
                onClose={() => setOpenSchedule(false)}
                device={selectedDevice}
                devices={devices}
                layouts={layouts}
                onSave={() => { onRefresh(); }}
            />
            <LogViewerDialog
                open={openLogs}
                onClose={() => setOpenLogs(false)}
                device={selectedDevice}
            />
        </Box>
    );
}

const DeviceCard = ({ d, layouts, onRefresh, selected, onToggleSelect, onLogs, onSchedule, onDelete, onCommand }) => (
    <Card sx={{
        position: 'relative',
        overflow: 'visible',
        transition: 'all 0.3s ease',
        '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 20px 40px rgba(0,0,0,0.4)' },
        ...(selected && { border: '2px solid #3b82f6', boxShadow: '0 0 20px rgba(59,130,246,0.3)' })
    }}>
        {selected !== undefined && (
            <Box sx={{ position: 'absolute', top: -10, right: 10, zIndex: 10 }}>
                <Checkbox 
                    checked={selected} 
                    onChange={onToggleSelect} 
                    sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#3b82f6' } }} 
                />
            </Box>
        )}
        {/* Status Pulsing Badge */}
        <Box sx={{
            position: 'absolute', top: -10, left: 24, px: 2, py: 0.5,
            bgcolor: d.status === 'online' ? '#10b981' : '#ef4444',
            borderRadius: 50,
            boxShadow: `0 4px 15px ${d.status === 'online' ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
            zIndex: 2,
            display: 'flex', alignItems: 'center', gap: 1
        }}>
            <Box sx={{
                width: 6, height: 6, bgcolor: 'white', borderRadius: '50%',
                animation: d.status === 'online' ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 } }
            }} />
            <Typography variant="caption" sx={{ fontWeight: 900, color: 'white', letterSpacing: 1 }}>{d.status === 'online' ? 'ACTIVE' : 'OFFLINE'}</Typography>
        </Box>

        <CardContent sx={{ pt: 4 }}>
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h6" fontWeight={900}>{d.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>HW-ID: {d.id.slice(0, 12)}</Typography>
                </Box>
                <Box>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<History />}
                        onClick={onLogs}
                        sx={{
                            color: '#10b981',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            fontSize: '0.6rem',
                            fontWeight: 900,
                            mr: 1,
                            '&:hover': { borderColor: '#10b981', bgcolor: 'rgba(16, 185, 129, 0.1)' }
                        }}
                    >
                        LOGS
                    </Button>
                    <IconButton size="small" title="Reiniciar App" onClick={() => onCommand(d.id, 'RESTART')} sx={{ color: '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.05)', mr: 1 }}><RestartAlt fontSize="small" /></IconButton>
                    <IconButton size="small" title="Pausar Reproducción" onClick={() => onCommand(d.id, 'PAUSE')} sx={{ color: '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.05)', mr: 1 }}><Pause fontSize="small" /></IconButton>
                    <IconButton size="small" title="Reanudar Reproducción" onClick={() => onCommand(d.id, 'RESUME')} sx={{ color: '#10b981', bgcolor: 'rgba(16, 185, 129, 0.05)', mr: 1 }}><PlayArrow fontSize="small" /></IconButton>
                    <IconButton size="small" title="Programar Reglas" onClick={onSchedule} sx={{ color: '#3b82f6', bgcolor: 'rgba(59, 130, 246, 0.05)', mr: 1 }}><EventNote fontSize="small" /></IconButton>
                    <IconButton size="small" title="Eliminar" onClick={onDelete} sx={{ color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.05)' }}><Delete fontSize="small" /></IconButton>
                </Box>
            </Box>

            <Divider sx={{ my: 2, opacity: 0.05 }} />

            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, px: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>CPU</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#3b82f6', fontWeight: 900 }}>{d.cpu_usage || 0}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={d.cpu_usage || 0} sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}>RAM</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 900 }}>{d.memory_usage || 0}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={d.memory_usage || 0} sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#10b981' } }} />
                </Box>
            </Box>

            <Box sx={{ px: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Uptime: <span style={{ color: 'rgba(255,255,255,0.7)' }}>{d.uptime || "N / A"}</span>
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', fontWeight: 800 }}>
                    IP: {d.ip || "Detectando..."}
                </Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">RESOLUCIÓN</Typography>
                    <Typography variant="body2" fontWeight={700}>{d.width}x{d.height}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">CANAL ACTUAL</Typography>
                    <Select
                        value={d.current_playlist_id || ""}
                        size="small"
                        fullWidth
                        sx={{
                            height: 28, fontSize: '0.7rem', fontWeight: 800,
                            bgcolor: 'rgba(255,255,255,0.03)',
                            '& .MuiSelect-select': { py: 0.5 }
                        }}
                        onChange={(e) => {
                            axios.post(`${API_URL}/devices/${d.id}/assign`, { playlistId: e.target.value })
                                .then(() => onRefresh());
                        }}
                        displayEmpty
                    >
                        <MenuItem value="" disabled>SIN ASIGNAR</MenuItem>
                        <ListSubheader sx={{ bgcolor: 'rgba(0,0,0,0.9)', color: '#3b82f6', fontWeight: 900, lineHeight: '32px' }}>DISEÑOS PRO</ListSubheader>
                        {layouts.map(l => (
                            <MenuItem key={`layout-${l.id}`} value={`layout-${l.id}`} sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                [PRO] {l.name}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            </Grid>
        </CardContent>
    </Card>
);

import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapResizer = ({ devices }) => {
    const map = useMap();
    const initialFit = useRef(false);
    
    useEffect(() => {
        if (!initialFit.current) {
            initialFit.current = true;
            
            if (devices && devices.length > 0) {
                const bounds = L.latLngBounds(devices.map(d => [d.lat, d.lon]));
                map.fitBounds(bounds, { padding: [50, 50] });
            }
            
            // Locate user precisely with HTML5 Geolocation API
            map.locate({ setView: devices.length === 0, maxZoom: 15, enableHighAccuracy: true });
            
            map.on('locationfound', (e) => {
                if (devices.length === 0) {
                    map.flyTo(e.latlng, 15);
                }
                const adminIcon = new L.DivIcon({
                    className: 'custom-div-icon',
                    html: `<div style="background-color: #3b82f6; width: 14px; height: 14px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 15px #3b82f6;"></div>`,
                    iconSize: [14, 14],
                    iconAnchor: [7, 7]
                });
                L.marker(e.latlng, { icon: adminIcon }).addTo(map)
                 .bindPopup("<div style='text-align:center;'><b>Tu Ubicación Precisa</b><br/>Puedes arrastrar terminales aquí</div>");
                L.circle(e.latlng, { radius: e.accuracy / 2, color: '#3b82f6', weight: 1, fillColor: '#3b82f6', fillOpacity: 0.1 }).addTo(map);
            });
        }
    }, [devices, map]);
    return null;
};

const getIcon = (status) => new L.DivIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color: ${status === 'online' ? '#10b981' : '#ef4444'}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 10px ${status === 'online' ? '#10b981' : '#ef4444'};"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6]
});

const DeviceMarker = ({ d, onSelect, onCommand }) => {
    return (
        <Marker key={d.id} position={[d.lat, d.lon]} icon={getIcon(d.status)} draggable={false}>
            <Popup>
                <Box sx={{ minWidth: 200, color: 'white', p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1, color: '#3b82f6' }}>{d.name.toUpperCase()}</Typography>
                    <Divider sx={{ mb: 1, opacity: 0.1 }} />
                    <Typography variant="caption" display="block">CPU: {d.cpu_usage || 0}% | RAM: {d.memory_usage || 0}%</Typography>
                    <Typography variant="caption" display="block">IP: {d.ip || "Detectando"}</Typography>
                    <Button variant="outlined" size="small" fullWidth sx={{ mt: 1, mb: 1 }} onClick={() => onSelect(d)}>
                        Ver Detalles
                    </Button>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <IconButton size="small" onClick={() => onCommand(d.id, 'RESTART')} sx={{ color: '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.05)' }}><RestartAlt fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => onCommand(d.id, 'PAUSE')} sx={{ color: '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.05)' }}><Pause fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => onCommand(d.id, 'RESUME')} sx={{ color: '#10b981', bgcolor: 'rgba(16, 185, 129, 0.05)' }}><PlayArrow fontSize="small" /></IconButton>
                    </Box>
                </Box>
            </Popup>
        </Marker>
    );
};

const InheritanceNode = ({ data, level = 0 }) => {
    const isDevice = data.type === 'device';
    const hasChildren = data.children && data.children.length > 0;
    
    return (
        <Box sx={{ ml: level > 0 ? 4 : 0, position: 'relative' }}>
            {level > 0 && (
                <Box sx={{ position: 'absolute', left: -20, top: 20, width: 20, height: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
            )}
            {level > 0 && (
                <Box sx={{ position: 'absolute', left: -20, top: -20, width: 2, height: 40, bgcolor: 'rgba(255,255,255,0.2)' }} />
            )}
            <Paper sx={{ 
                p: 1.5, my: 1, 
                display: 'inline-flex', alignItems: 'center', gap: 2,
                bgcolor: isDevice ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                border: `1px solid ${isDevice ? '#10b981' : '#3b82f6'}`,
                borderRadius: 2
            }}>
                {isDevice ? <Tv sx={{ color: '#10b981' }} /> : <ViewList sx={{ color: '#3b82f6' }} />}
                <Box>
                    <Typography variant="body2" fontWeight={800}>{data.name}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>{isDevice ? 'Terminal' : 'Playlist'}</Typography>
                </Box>
            </Paper>
            {hasChildren && (
                <Box sx={{ borderLeft: '2px solid rgba(255,255,255,0.1)', ml: 3, pt: 1 }}>
                    {data.children.map((child, i) => (
                        <InheritanceNode key={i} data={child} level={level + 1} />
                    ))}
                </Box>
            )}
        </Box>
    );
};

function buildLayoutNode(layout, layouts, visited, depth = 0) {
    if (visited.has(layout.id)) {
        return { type: 'playlist', name: layout.name + ' (Ciclo Detectado)', children: [] };
    }
    visited.add(layout.id);

    let children = [];
    if (layout.nested_layouts && Array.isArray(layout.nested_layouts) && layout.nested_layouts.length > 0) {
        layout.nested_layouts.forEach(src => {
            const subLayout = layouts.find(l => l.id.toString() === src.toString());
            if (subLayout) {
                children.push(buildLayoutNode(subLayout, layouts, new Set(visited), depth + 1));
            } else {
                children.push({ type: 'playlist', name: `Playlist [Eliminada: ${src}]`, children: [] });
            }
        });
    }

    return { type: 'playlist', name: layout.name, children };
}

function NodeGraphView({ devices, layouts }) {
    const tree = devices.map(d => {
        let children = [];
        if (d.current_playlist_id) {
            const layoutId = d.current_playlist_id.replace('layout-', '');
            const layout = layouts.find(l => l.id.toString() === layoutId);
            if (layout) {
                console.log('[NodeGraphView] Building tree for device:', d.name, 'layout:', layout.name, 'layoutId:', layout.id, 'nested_layouts:', layout.nested_layouts);
                console.log('[NodeGraphView] All layouts:', layouts.map(l => ({ id: l.id, name: l.name, nested_layouts: l.nested_layouts })));
                children.push(buildLayoutNode(layout, layouts, new Set(), 0));
            }
        }
        return { type: 'device', name: d.name, children };
    });

    console.log('[NodeGraphView] Final tree:', JSON.stringify(tree, null, 2));

    return (
        <GlassContainer sx={{ p: 4, overflow: 'auto', maxHeight: '70vh', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="overline" sx={{ mb: 3, display: 'block', color: 'rgba(255,255,255,0.5)', fontWeight: 900 }}>
                JERARQUÍA DE NODOS Y ASIGNACIONES
            </Typography>
            {tree.map((node, i) => <InheritanceNode key={i} data={node} />)}
        </GlassContainer>
    );
}

function TopologyView({ devices, onSelect, onCommand }) {

    return (
        <GlassContainer sx={{ p: 0, position: 'relative', overflow: 'hidden', height: 600, border: '1px solid rgba(255,255,255,0.1)' }}>
            <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1000 }}>
                <GlassContainer sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, bgcolor: '#10b981', borderRadius: '50%' }} />
                        <Typography variant="caption" fontWeight={900}>ONLINE</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, bgcolor: '#ef4444', borderRadius: '50%' }} />
                        <Typography variant="caption" fontWeight={900}>OFFLINE</Typography>
                    </Box>
                </GlassContainer>
            </Box>

            <MapContainer center={[40.41, -3.70]} zoom={6} style={{ height: '100%', width: '100%', background: '#050510' }} scrollWheelZoom={true}>
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />
                <MapResizer devices={devices} />
                {devices.map((d) => (
                    <DeviceMarker key={d.id} d={d} onSelect={onSelect} onCommand={onCommand} />
                ))}
            </MapContainer>
            
            {/* CSS Injection for Leaflet Popups to match commercial UI */}
            <style dangerouslySetInnerHTML={{ __html: `
                .leaflet-popup-content-wrapper { background: rgba(10, 10, 25, 0.9) !important; backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; color: white !important; }
                .leaflet-popup-tip { background: rgba(10, 10, 25, 0.9) !important; }
                .leaflet-container { font-family: 'Outfit', sans-serif !important; }
                .leaflet-bar { border: none !important; box-shadow: 0 4px 15px rgba(0,0,0,0.4) !important; }
                .leaflet-bar a { background-color: rgba(30,30,45,0.8) !important; color: white !important; border-bottom: 1px solid rgba(255,255,255,0.05) !important; }
                .leaflet-bar a:hover { background-color: #3b82f6 !important; }
            `}} />
        </GlassContainer>
    );
}

function LogViewerDialog({ open, onClose, device }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && device) {
            fetchLogs();
            const interval = setInterval(fetchLogs, 3000);
            return () => clearInterval(interval);
        }
    }, [open, device]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/devices/${device.id}/logs`);
            if (Array.isArray(res.data)) setLogs(res.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { background: 'rgba(5, 5, 10, 0.95)', backdropFilter: 'blur(30px)', border: '1px solid rgba(255,255,255,0.05)' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: -0.5 }}>TERMINAL TELEMETRY: {device?.name}</Typography>
                <Chip label="LIVE" size="small" color="success" sx={{ fontWeight: 900, height: 20, fontSize: '0.6rem' }} />
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.05)', p: 0 }}>
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.3)', p: 2, height: 400, overflowY: 'auto', fontFamily: 'monospace' }}>
                    {(!Array.isArray(logs) || logs.length === 0) ? (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>Esperando transmisiones del terminal...</Typography>
                    ) : (
                        logs.map((log, idx) => (
                            <Box key={idx} sx={{ mb: 1, display: 'flex', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.02)', pb: 1 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', minWidth: 150 }}>
                                    {new Date(log.timestamp).toLocaleString('es-ES')}
                                </Typography>
                                <Typography variant="caption" sx={{
                                    fontWeight: 900,
                                    color: log.level === 'ERROR' ? '#ef4444' : log.level === 'WARN' ? '#f59e0b' : '#3b82f6',
                                    minWidth: 50
                                }}>
                                    {log.level}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    {log.message}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="contained">Cerrar Consola</Button>
            </DialogActions>
        </Dialog>
    );
}

function ScheduleDialog({ open, onClose, device, devices, layouts, onSave }) {
    const [rules, setRules] = useState([]);
    const [cloneTarget, setCloneTarget] = useState('');

    useEffect(() => {
        if (device && device.schedule_rules) {
            try {
                let parsed = JSON.parse(device.schedule_rules);
                const migrated = parsed.map(r => ({
                    ...r,
                    playlistId: r.playlistId?.toString().startsWith('layout-') ? r.playlistId : `layout-${r.playlistId}`,
                    active: r.active !== false // default to true if undefined
                }));
                setRules(migrated);
            } catch (e) { setRules([]); }
        } else {
            setRules([]);
        }
        setCloneTarget('');
    }, [device, open]);

    const addRule = () => {
        setRules([...rules, { days: [1, 2, 3, 4, 5], start: "09:00", end: "21:00", playlistId: "", active: true }]);
    };

    const handleUpdateRule = (idx, updates) => {
        const newRules = [...rules];
        newRules[idx] = { ...newRules[idx], ...updates };
        setRules(newRules);
    };

    const removeRule = (idx) => {
        setRules(rules.filter((_, i) => i !== idx));
    };

    const saveRules = () => {
        axios.post(`${API_URL}/devices/${device.id}/rules`, { rules })
            .then(() => { onSave(); onClose(); })
            .catch(err => alert("Error: " + err.message));
    };

    const dayLabels = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { background: 'rgba(20, 20, 35, 0.95)', backdropFilter: 'blur(20px)' } }}>
            <DialogTitle sx={{ fontWeight: 800 }}>REGLAS CRONOLÓGICAS: {device?.name}</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    {rules.map((rule, idx) => (
                        <GlassContainer key={idx} sx={{ p: 2, position: 'relative', opacity: rule.active === false ? 0.5 : 1, transition: 'all 0.3s' }}>
                            <Box sx={{ position: 'absolute', top: 5, right: 5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FormControlLabel 
                                    control={<Switch size="small" checked={rule.active !== false} onChange={e => handleUpdateRule(idx, { active: e.target.checked })} />} 
                                    label={<Typography variant="caption" sx={{ fontWeight: 800 }}>{rule.active !== false ? 'ACTIVO' : 'INACTIVO'}</Typography>}
                                    sx={{ mr: 1, m: 0 }}
                                />
                                <IconButton size="small" color="error" onClick={() => removeRule(idx)}><Delete fontSize="small" /></IconButton>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <Box display="flex" gap={0.5}>
                                        {[1, 2, 3, 4, 5, 6, 0].map(day => (
                                            <Chip
                                                key={day} label={dayLabels[day]} size="small"
                                                color={rule.days.includes(day) ? "primary" : "default"}
                                                onClick={() => {
                                                    const newDays = rule.days.includes(day)
                                                        ? rule.days.filter(d => d !== day)
                                                        : [...rule.days, day];
                                                    handleUpdateRule(idx, { days: newDays });
                                                }}
                                                sx={{ fontWeight: 800, borderRadius: 1.5 }}
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField label="Inicio" type="time" size="small" value={rule.start} onChange={e => handleUpdateRule(idx, { start: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField label="Fin" type="time" size="small" value={rule.end} onChange={e => handleUpdateRule(idx, { end: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Canal de Destino</InputLabel>
                                        <Select value={rule.playlistId} label="Canal de Destino" onChange={e => handleUpdateRule(idx, { playlistId: e.target.value })}>
                                            {layouts.map(l => (
                                                <MenuItem key={`layout-${l.id}`} value={`layout-${l.id}`} sx={{ fontWeight: 800 }}>
                                                    <ViewList fontSize="small" sx={{ mr: 1, opacity: 0.5 }} /> {l.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </GlassContainer>
                    ))}
                </Stack>
                <Button startIcon={<Add />} onClick={addRule} sx={{ mt: 3, color: '#3b82f6' }}>Añadir Disparador de Secuencia</Button>
            </DialogContent>
            <DialogActions sx={{ p: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Select size="small" value={cloneTarget} onChange={e => setCloneTarget(e.target.value)} displayEmpty sx={{ width: 220, bgcolor: 'rgba(255,255,255,0.05)' }}>
                        <MenuItem value="" disabled>Copiar este horario a...</MenuItem>
                        {devices?.filter(d => d.id !== device?.id).map(d => (
                            <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                        ))}
                    </Select>
                    <Button 
                        startIcon={<ContentCopy />} size="small" variant="outlined" 
                        disabled={!cloneTarget} 
                        onClick={() => {
                            if (!window.confirm('Esto sobrescribirá las reglas del terminal destino. ¿Continuar?')) return;
                            axios.post(`${API_URL}/devices/${cloneTarget}/rules`, { rules })
                                .then(() => alert('Reglas copiadas con éxito en el terminal destino'))
                                .catch(err => alert("Error: " + err.message));
                        }}
                    >Copiar</Button>
                </Box>
                <Box>
                    <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)', mr: 1 }}>Cancelar</Button>
                    <Button onClick={saveRules} variant="contained" sx={{ boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>Confirmar Reglas</Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

function MediaPreviewDialog({ media, onClose }) {
    if (!media) return null;
    return (
        <Dialog open={!!media} onClose={onClose} maxWidth="lg" PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={onClose}>
                {media.type === 'image' ? (
                    <img src={`uploads/${media.filename}`} style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }} alt="Preview" />
                ) : (
                    <video src={`uploads/${media.filename}`} controls autoPlay style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
                )}
            </Box>
        </Dialog>
    );
}

function MediaTab({ media, onRefresh }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [filterType, setFilterType] = useState('all');
    const [previewMedia, setPreviewMedia] = useState(null);

    const deleteMedia = (id) => {
        if (confirm("¿Confirmar eliminación de recurso?")) {
            axios.delete(`${API_URL}/media/${id}`).then(onRefresh);
        }
    };

    const onDrop = (files) => {
        const formData = new FormData();
        formData.append('file', files[0]);
        axios.post(`${API_URL}/upload`, formData).then(onRefresh);
    };

    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    // --- Lógica de Organización Virtual ---
    const filteredMedia = media.filter(m => 
        m.original_name.toLowerCase().includes(search.toLowerCase()) &&
        (filterType === 'all' || m.type === filterType)
    );

    const groupedMedia = filteredMedia.reduce((acc, m) => {
        const prefix = m.original_name.includes('_') ? m.original_name.split('_')[0] : 'General';
        const folder = m.type === 'video' ? `Videos / ${prefix}` : `Imágenes / ${prefix}`;
        if (!acc[folder]) acc[folder] = [];
        acc[folder].push(m);
        return acc;
    }, {});

    return (
        <Box sx={{ px: isMobile ? 0 : 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={900}>Recursos</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.5 }}>Gestión de activos multimedia y colecciones</Typography>
                </Box>
                <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <TextField 
                        size="small" placeholder="Buscar recurso..." 
                        value={search} onChange={e => setSearch(e.target.value)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}
                    />
                    <Select size="small" value={filterType} onChange={e => setFilterType(e.target.value)} sx={{ minWidth: 120 }}>
                        <MenuItem value="all">TODOS</MenuItem>
                        <MenuItem value="image">IMÁGENES</MenuItem>
                        <MenuItem value="video">VIDEOS</MenuItem>
                    </Select>
                    <IconButton onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                        {viewMode === 'grid' ? <ViewList /> : <Dns />}
                    </IconButton>
                </Stack>
            </Box>

            <Box {...getRootProps()} sx={{
                border: '2px dashed rgba(59, 130, 246, 0.2)',
                p: 4, textAlign: 'center', mb: 4,
                borderRadius: 4, cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: 'linear-gradient(145deg, rgba(59, 130, 246, 0.05), transparent)',
                '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.08)', borderColor: '#3b82f6' }
            }}>
                <input {...getInputProps()} />
                <CloudUpload fontSize="large" sx={{ color: '#3b82f6', mb: 1 }} />
                <Typography variant="h6" fontWeight={800}>Añadir Activos</Typography>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>ARRASTRE AQUÍ SUS ARCHIVOS (MÁX 100MB)</Typography>
            </Box>

            {Object.keys(groupedMedia).sort().map(folder => (
                <Box key={folder} sx={{ mb: 4 }}>
                    <Typography variant="overline" sx={{ fontWeight: 900, color: '#3b82f6', mb: 2, display: 'block', letterSpacing: 2 }}>
                        {folder.toUpperCase()} <span style={{ opacity: 0.3 }}>({groupedMedia[folder].length})</span>
                    </Typography>
                    
                    {viewMode === 'grid' ? (
                        <Grid container spacing={2}>
                            {groupedMedia[folder].map(m => (
                                <Grid item xs={6} sm={4} md={3} lg={2} key={m.id}>
                                    <Grow in={true}>
                                        <Card sx={{ 
                                            position: 'relative', bgcolor: 'rgba(255,255,255,0.02)',
                                            '&:hover .media-actions': { opacity: 1 }
                                        }}>
                                            <Box sx={{ pt: '100%', position: 'relative', overflow: 'hidden', cursor: 'pointer' }} onClick={() => setPreviewMedia(m)}>
                                                {m.type === 'image' ? (
                                                    <CardMedia component="img" image={`/uploads/${m.filename}`} sx={{ position: 'absolute', top: 0, height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <Box sx={{ position: 'absolute', top: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}>
                                                        <PlayArrow sx={{ fontSize: 40, opacity: 0.2 }} />
                                                    </Box>
                                                )}
                                                <Box className="media-actions" sx={{ 
                                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                                                    bgcolor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', 
                                                    justifyContent: 'center', opacity: 0, transition: '0.2s' 
                                                }}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteMedia(m.id); }} sx={{ color: '#ef4444' }}><Delete /></IconButton>
                                                </Box>
                                            </Box>
                                            <Box sx={{ p: 1, textAlign: 'center' }}>
                                                <Typography variant="caption" noWrap display="block" sx={{ fontWeight: 700 }}>{m.original_name}</Typography>
                                            </Box>
                                        </Card>
                                    </Grow>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Paper sx={{ bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 3 }}>
                            <List disablePadding>
                                {groupedMedia[folder].map((m, idx) => (
                                    <ListItem key={m.id} divider={idx < groupedMedia[folder].length - 1} sx={{ borderColor: 'rgba(255,255,255,0.05)', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }} onClick={() => setPreviewMedia(m)}>
                                        <ListItemIcon>
                                            {m.type === 'image' ? <Folder sx={{ color: '#10b981' }} /> : <PlayArrow sx={{ color: '#3b82f6' }} />}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={m.original_name} 
                                            secondary={`${m.type.toUpperCase()} • ${m.filename}`}
                                            primaryTypographyProps={{ fontWeight: 800, fontSize: '0.8rem' }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteMedia(m.id); }} color="error"><Delete fontSize="small" /></IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Box>
            ))}
            <MediaPreviewDialog media={previewMedia} onClose={() => setPreviewMedia(null)} />
        </Box>
    );
}


const LayoutPreview = ({ zones, resolution }) => {
    return (
        <Box sx={{ 
            width: '100%', height: '100%', position: 'relative', 
            bgcolor: '#050510', border: '1px solid rgba(255,255,255,0.05)' 
        }}>
            {zones.map((z, idx) => (
                <Box key={idx} sx={{
                    position: 'absolute',
                    left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%`,
                    bgcolor: z.type === 'video' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(16, 185, 129, 0.4)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: z.z
                }}>
                    <Typography sx={{ fontSize: '0.4rem', opacity: 0.5, fontWeight: 900 }}>
                        {z.type.toUpperCase()}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

function LayoutsTab({ layouts, onRefresh, onIdCreated }) {
    const [activeLayoutId, setActiveLayoutId] = useState(null);

    if (activeLayoutId) {
        return <TimelineEditor
            layoutId={activeLayoutId === 'new' ? null : activeLayoutId}
            onBack={() => { setActiveLayoutId(null); onRefresh(); }}
            onIdCreated={(newId) => { setActiveLayoutId(newId); onIdCreated(newId); }}
        />;
    }

    return (
        <Box sx={{ px: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight={900}>Composiciones</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.5 }}>Gestión de diseños multicapa y NLE</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setActiveLayoutId('new')}
                    sx={{ boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)', borderRadius: 2, px: 3 }}
                >
                    NUEVO DISEÑO
                </Button>
            </Box>

            <Grid container spacing={3}>
                {layouts.length === 0 && (
                    <Grid item xs={12}>
                        <Box sx={{ p: 5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 4, border: '1px dashed rgba(255,255,255,0.1)' }}>
                            <Typography variant="h6" sx={{ opacity: 0.3 }}>No hay composiciones creadas</Typography>
                            <Button sx={{ mt: 2 }} onClick={() => setActiveLayoutId('new')}>Crear Primera Composición</Button>
                        </Box>
                    </Grid>
                )}
                {layouts.map(l => (
                    <Grid item xs={12} sm={6} md={4} key={l.id}>
                        <GlassContainer sx={{
                            p: 0, overflow: 'hidden', cursor: 'pointer',
                            '&:hover': { transform: 'translateY(-5px)', borderColor: '#3b82f6' },
                            transition: 'all 0.3s'
                        }} onClick={() => setActiveLayoutId(l.id)}>
                            <Box sx={{ pt: '56.25%', bgcolor: '#000', position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                    <LayoutPreview zones={l.zones || []} resolution={l.resolution} />
                                </Box>
                                <Chip label="PRO" size="small" sx={{ position: 'absolute', top: 10, right: 10, bgcolor: '#3b82f6', fontWeight: 900, zIndex: 10 }} />
                            </Box>
                            <Box sx={{ p: 2 }}>
                                <Typography fontWeight={900} sx={{ mb: 0.5 }}>{l.name.toUpperCase()}</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.5, display: 'block' }}>
                                    {new Date(l.created_at).toLocaleDateString()} • {l.resolution?.width}x{l.resolution?.height}
                                </Typography>
                                <Divider sx={{ my: 1.5, opacity: 0.05 }} />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Button size="small" sx={{ color: '#3b82f6' }} onClick={(e) => { e.stopPropagation(); setActiveLayoutId(l.id); }}>EDITAR DISEÑO</Button>
                                        <IconButton size="small" sx={{ color: '#10b981', ml: 1 }} onClick={(e) => {
                                            e.stopPropagation();
                                            const payload = { name: `${l.name} (Copia)`, description: l.description, resolution: l.resolution, zones: l.zones };
                                            axios.post(`${API_URL}/layouts`, payload).then(() => onRefresh()).catch(err => alert("Error: " + err.message));
                                        }} title="Duplicar"><ContentCopy fontSize="small" /></IconButton>
                                    </Box>
                                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); if (window.confirm('¿Eliminar composición?')) axios.delete(`${API_URL}/layouts/${l.id}`).then(() => onRefresh()); }}><Delete fontSize="small" /></IconButton>
                                </Stack>
                            </Box>
                        </GlassContainer>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

function AlertSettingsDialog({ open, onClose, offlineDevices }) {
    const [botToken, setBotToken] = useState('');
    const [chatId, setChatId] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testStatus, setTestStatus] = useState(null); // null | 'success' | 'error'

    useEffect(() => {
        if (open) {
            axios.get(`${API_URL}/admin/alert-config`).then(res => {
                setBotToken(res.data.botToken || '');
                setChatId(res.data.chatId || '');
                setEnabled(res.data.enabled || false);
                setTestStatus(null);
            }).catch(() => {});
        }
    }, [open]);

    const handleSave = async () => {
        setSaving(true);
        setTestStatus(null);
        try {
            const res = await axios.post(`${API_URL}/admin/alert-config`, { botToken, chatId, enabled });
            if (res.data.success) {
                setTestStatus('success');
            }
        } catch (err) {
            setTestStatus('error');
        }
        setSaving(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { background: 'rgba(10, 10, 25, 0.97)', backdropFilter: 'blur(30px)', border: '1px solid rgba(255,255,255,0.07)' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <NotificationsActive sx={{ color: '#3b82f6' }} />
                <Typography fontWeight={900} letterSpacing={-0.5}>Sistema de Alertas</Typography>
                <Chip
                    label={enabled ? 'ACTIVO' : 'INACTIVO'}
                    size="small"
                    sx={{ ml: 'auto', bgcolor: enabled ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)', color: enabled ? '#10b981' : 'rgba(255,255,255,0.3)', fontWeight: 900, border: enabled ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(255,255,255,0.05)' }}
                />
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    {/* Offline device summary */}
                    {offlineDevices.length > 0 && (
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: '#ef4444', display: 'block', mb: 1 }}>
                                🔴 {offlineDevices.length} TERMINAL{offlineDevices.length > 1 ? 'ES' : ''} OFFLINE AHORA
                            </Typography>
                            {offlineDevices.map(d => (
                                <Typography key={d.id} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.6)', pl: 1 }}>
                                    • {d.name} — IP: {d.ip || 'N/A'}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    {offlineDevices.length === 0 && (
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)', textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 900 }}>✅ Todos los terminales están en línea</Typography>
                        </Box>
                    )}

                    {/* How to guide */}
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.15)' }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: '#3b82f6', display: 'block', mb: 0.5 }}>📖 Cómo configurar</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>1. Abre <b>@BotFather</b> en Telegram → /newbot → copia el token</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>2. Escribe a tu bot desde la cuenta de <b>cada manager</b> que quiera recibir alertas</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>3. Cada uno abre <b>@userinfobot</b> → copia su Chat ID numérico</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>4. Pega todos los IDs separados por coma en el campo de abajo</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 0.5 }}>💡 Alternativa rápida: crea un grupo Telegram con el bot y usa el Chat ID del grupo (empieza por -100)</Typography>
                    </Box>

                    <TextField
                        label="Bot Token (@BotFather)"
                        fullWidth size="small"
                        value={botToken}
                        onChange={e => setBotToken(e.target.value)}
                        placeholder="123456789:AAF..."
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                    <TextField
                        label="Chat IDs de Receptores"
                        fullWidth size="small"
                        value={chatId}
                        onChange={e => setChatId(e.target.value)}
                        placeholder="12345678, -100987654321, 87654321"
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                        helperText="Uno o más IDs separados por coma. Grupos: -100... | Personas: ID numérico de @userinfobot"
                    />
                    <FormControlLabel
                        control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} color="success" />}
                        label={<Typography variant="body2" fontWeight={800}>{enabled ? 'Alertas habilitadas' : 'Alertas deshabilitadas'}</Typography>}
                    />

                    {testStatus === 'success' && (
                        <Alert severity="success" sx={{ bgcolor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                            ✅ Configuración guardada. Revisa Telegram — deberías haber recibido un mensaje de prueba.
                        </Alert>
                    )}
                    {testStatus === 'error' && (
                        <Alert severity="error" sx={{ bgcolor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                            ❌ Error al guardar o enviar. Revisa el token y chat ID.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)' }}>Cerrar</Button>
                <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Send />}
                    onClick={handleSave}
                    disabled={saving || !botToken || !chatId}
                    sx={{ boxShadow: '0 4px 12px rgba(59,130,246,0.3)' }}
                >
                    {saving ? 'Guardando...' : 'Guardar y Probar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default App;


